'use client';

import { useState } from 'react';
import { useTranslations } from 'next-intl';
import {
  Brain,
  Calculator,
  Check,
  ChevronRight,
  ClipboardList,
  FileText,
  Globe,
  PencilRuler,
} from 'lucide-react';
import { useSubscriptionLimits } from '@/features/subscription';
import { usePassPaywallStore } from '@/shared/stores/pass-paywall-store';
import { NoticeBanner } from '@/shared/ui/NoticeBanner';
import { Textarea } from '@/shared/ui/shadcn/textarea';
import { Switch } from '@/shared/ui/shadcn/switch';
import { SettingsCard } from '@/shared/ui/settings/SettingsCard';
import {
  NestedSheet,
  NestedSheetContent,
  NestedSheetHeader,
  NestedSheetTitle,
  NestedSheetBody,
} from '@/shared/ui/NestedSheet';
import { ProBadge } from '@/features/subscription/ui/ProBadge';
import { cn } from '@/shared/lib/cn';
import { sortAlbumLocalesForUser, getLocaleByCode } from '@/shared/constants';
import {
  isPremiumAlbumTemplate,
  MATERIALS_SECTION_TOGGLE_ROW,
  SECONDARY_SECTION_TOGGLE_ROWS,
  type AlbumMeasurementSystem,
  type AlbumSectionToggleKey,
  type AlbumSectionToggleRow,
  type AlbumSectionToggles,
  type AlbumTemplate,
} from '@/features/design/album';
import type { AlbumEstimatePicker } from '../hooks/use-album-estimate-picker';
import { AlbumEstimateOptions } from './AlbumEstimateOptions';
import { AlbumRenderThumbnailStrip } from './AlbumRenderThumbnailStrip';

// Render order for the template (design) axis — `standard` free, the rest are
// premium (Designer-tier). Mirrors Apple/Android catalog order.
const ALBUM_TEMPLATE_OPTIONS: readonly AlbumTemplate[] = [
  'standard',
  'architect',
  'studio',
];

// Units picker order — Auto (server decides, default) first, then the two
// explicit systems. Mirrors Apple/Android.
const ALBUM_MEASUREMENT_SYSTEM_OPTIONS: readonly AlbumMeasurementSystem[] = [
  'auto',
  'metric',
  'imperial',
];

interface AlbumPanelSettingsProps {
  // Requirements
  requirements: string;
  onRequirementsChange: (value: string) => void;
  isArchived: boolean;

  // Template (design axis — only `standard` selectable)
  selectedTemplate: AlbumTemplate;
  onTemplateChange: (template: AlbumTemplate) => void;

  // Content-section toggles (materials / blueprints / preparation guide /
  // safety requirements / building regulations) — free, all default ON.
  sectionToggles: AlbumSectionToggles;
  onSectionToggleChange: (
    key: AlbumSectionToggleKey,
    enabled: boolean
  ) => void;

  // Language
  selectedLanguage: string;
  onLanguageChange: (language: string) => void;

  // Plan features
  hasAdvancedAlbumOptions: boolean;
  // Designer-tier access to premium album DESIGN templates (architect/studio).
  // With access the premium rows are freely selectable (no badge); without it
  // each shows a DESIGNER lock badge and a tap routes to the Designer Pass
  // paywall. Mirrors the closed named-model row.
  isAlbumTemplatesAvailable: boolean;

  // Extended thinking (Pro+ gated)
  isExtendedThinking: boolean;
  onIsExtendedThinkingChange: (enabled: boolean) => void;

  // Units (measurement system) — free, default Auto (server decides; the
  // field is omitted from the generate request).
  measurementSystem: AlbumMeasurementSystem;
  onMeasurementSystemChange: (system: AlbumMeasurementSystem) => void;

  // Partial renders warning (rendered below cost-disclaimer to match Apple).
  hasPartialRenders: boolean;
  renderedCount: number;
  totalCount: number;

  // Thumbnails of the renders that will be packed into the album — shown at the
  // top of the panel so the selection is visible before any setting.
  selectedRenderUrls: string[];

  // Cost-estimate picker (FE4): this design's estimates first (default),
  // "New estimate", then sibling designs' estimates greyed at the end.
  estimatePicker: AlbumEstimatePicker;

  // State
  isGenerating: boolean;

  // Server-localized significant-cost warning (GET /album/cost-prediction
  // `warning_message`) — null when the predicted cost is below the
  // server-side threshold, while a prediction is loading, or on error. The
  // client never composes this text (API-driven messages rule).
  costWarningMessage: string | null;
}

export function AlbumPanelSettings({
  requirements,
  onRequirementsChange,
  isArchived,
  selectedTemplate,
  onTemplateChange,
  sectionToggles,
  onSectionToggleChange,
  selectedLanguage,
  onLanguageChange,
  hasAdvancedAlbumOptions,
  isAlbumTemplatesAvailable,
  isExtendedThinking,
  onIsExtendedThinkingChange,
  measurementSystem,
  onMeasurementSystemChange,
  hasPartialRenders,
  renderedCount,
  totalCount,
  selectedRenderUrls,
  estimatePicker,
  isGenerating,
  costWarningMessage,
}: AlbumPanelSettingsProps) {
  const t = useTranslations('album');
  const tEstimate = useTranslations('common.estimate');
  // Reuse the render Extended Thinking copy (already translated in all 24
  // locales) — keeps full locale parity with zero new album strings.
  const tThinking = useTranslations('render.settings.extendedThinking');

  // Extended thinking is a Pro perk (EntitlementFeatureMap: extended_thinking).
  // A locked tap upsells the Pro Pass — mirrors the render ExtendedThinkingSection.
  const limits = useSubscriptionLimits();
  const hasExtendedThinking = limits?.has_extended_thinking ?? false;
  const openPassPaywall = usePassPaywallStore((s) => s.open);

  const handleExtendedThinkingRowClick = () => {
    if (!hasExtendedThinking) {
      openPassPaywall('pro');
      return;
    }
    onIsExtendedThinkingChange(!isExtendedThinking);
  };

  const handleExtendedThinkingSwitchChange = (checked: boolean) => {
    if (!hasExtendedThinking) {
      openPassPaywall('pro');
      return;
    }
    onIsExtendedThinkingChange(checked);
  };

  const [isTemplateOpen, setIsTemplateOpen] = useState(false);
  const [isRequirementsOpen, setIsRequirementsOpen] = useState(false);
  const [isLanguageOpen, setIsLanguageOpen] = useState(false);
  const [isEstimateOpen, setIsEstimateOpen] = useState(false);
  const [isUnitsOpen, setIsUnitsOpen] = useState(false);

  // Cost-estimate picker (FE4) — resolve the row's trailing label: the
  // localized document name when one is picked (estimates carry no user-set
  // title — Apple/Android row-value parity), "New estimate", or "Don't
  // include" (also the forced value while the plan lacks the cost_estimate
  // feature).
  const {
    currentDesignEstimates,
    otherDesignEstimates,
    selectedEstimateId,
    setSelectedEstimateId,
    isEstimateIncluded,
    excludeEstimate,
    isPlanLocked: isEstimateLocked,
  } = estimatePicker;
  const selectedEstimate = [
    ...currentDesignEstimates,
    ...otherDesignEstimates,
  ].find((e) => e.id === selectedEstimateId);
  const estimateRowValue =
    isEstimateLocked || !isEstimateIncluded
      ? tEstimate('album.dontInclude')
      : selectedEstimate
        ? tEstimate('card.untitled')
        : tEstimate('album.generateNew');
  // Sorted once when sheet opens — stable during selection
  const [languageSheetOrder, setLanguageSheetOrder] = useState(() =>
    sortAlbumLocalesForUser(selectedLanguage)
  );
  const selectedLanguageName =
    getLocaleByCode(selectedLanguage)?.name ?? selectedLanguage;

  const openLanguageSheet = () => {
    setLanguageSheetOrder(sortAlbumLocalesForUser(selectedLanguage));
    setIsLanguageOpen(true);
  };

  // Content-section toggle row — free Switch row with a trailing divider.
  // Rendered in two slices (Materials first, the rest after the Estimate
  // row), so the markup lives in one place.
  const renderSectionToggleRow = ({
    key,
    icon: Icon,
    labelKey,
  }: AlbumSectionToggleRow) => (
    <div key={key}>
      <div className="px-5 py-3">
        <div className="flex items-center gap-3">
          <Icon className="h-5 w-5 text-primary shrink-0" strokeWidth={1.5} />
          <span className="text-list-item-subtitle flex-1 text-left">
            {t(`settings.${labelKey}.title`)}
          </span>
          <Switch
            checked={sectionToggles[key]}
            onCheckedChange={(checked) => onSectionToggleChange(key, checked)}
            disabled={isGenerating}
            aria-label={t(`settings.${labelKey}.title`)}
          />
        </div>
      </div>
      <div className="border-t border-border/30 mx-5" />
    </div>
  );

  return (
    // No horizontal padding: this panel renders only on the single-column
    // concept page, which already supplies the page gutter (px-4 sm:px-6
    // lg:px-8). An inner px-3 doubled it — the settings sat narrower than the
    // sibling albums list and the Estimate tab. `relative` hosts the
    // drill-down NestedSheets (their own px-4 is unaffected).
    <div className="relative space-y-3 py-3">
      {/* Renders that will be packed into the album — shown right after the
          heading so the selection is visible before any setting. */}
      <AlbumRenderThumbnailStrip urls={selectedRenderUrls} />

      {/* Partial-renders warning — shown only when some angles still lack a
          render, so the user knows the album won't cover every view. */}
      {hasPartialRenders && (
        <NoticeBanner
          variant="info"
          message={t('partialRendersWarning', { renderedCount, totalCount })}
        />
      )}

      {/* Settings group */}
      <SettingsCard>
        {/* Template Row (design axis) */}
        <button
          onClick={() => setIsTemplateOpen(true)}
          disabled={isGenerating}
          className="w-full flex items-center gap-3 px-5 py-3 hover:bg-muted/50 transition-colors"
        >
          <FileText className="h-5 w-5 text-primary shrink-0" strokeWidth={1.5} />
          <span className="text-list-item-subtitle flex-1 text-left">
            {t('settings.type.label')}
          </span>
          <span className="text-list-item-subtitle text-muted-foreground">
            {t(`settings.type.${selectedTemplate}`)}
          </span>
          <ChevronRight className="h-4 w-4 text-muted-foreground flex-shrink-0 rtl:-scale-x-100" />
        </button>

        <div className="border-t border-border/30 mx-5" />

        {/* Language Row */}
        <button
          onClick={openLanguageSheet}
          disabled={isGenerating}
          className="w-full flex items-center gap-3 px-5 py-3 hover:bg-muted/50 transition-colors"
        >
          <Globe className="h-5 w-5 text-primary shrink-0" strokeWidth={1.5} />
          <span className="text-list-item-subtitle flex-1 text-left">
            {t('settings.language')}
          </span>
          <span className="text-list-item-subtitle text-muted-foreground">
            {selectedLanguageName}
          </span>
          <ChevronRight className="h-4 w-4 text-muted-foreground flex-shrink-0 rtl:-scale-x-100" />
        </button>

        <div className="border-t border-border/30 mx-5" />

        {/* Content-section toggles — materials / blueprints / preparation
            guide / safety requirements / building regulations. Free Switch
            rows, all default ON; `include_blueprints` gates ONLY the facade
            projections. The Estimate row sits right after Materials —
            order mirrors Apple/Android. */}
        {renderSectionToggleRow(MATERIALS_SECTION_TOGGLE_ROW)}

        {/* Estimate Row (FE4) — defaults to this design's latest ready
            estimate; the sheet also offers "Don't include", "New estimate"
            and sibling designs' estimates. Estimate-in-album is a Pro perk
            (cost_estimate): when locked the PRO badge is the only marker,
            the value reads "Don't include" and a tap routes to the Pro Pass
            paywall — mirrors the Requirements row. */}
        <button
          onClick={
            isEstimateLocked
              ? () => openPassPaywall('pro')
              : () => setIsEstimateOpen(true)
          }
          disabled={isGenerating}
          className="w-full flex items-center gap-3 px-5 py-3 hover:bg-muted/50 transition-colors"
        >
          <Calculator className="h-5 w-5 text-primary shrink-0" strokeWidth={1.5} />
          <span className="text-list-item-subtitle text-left">
            {tEstimate('album.label')}
          </span>
          <ProBadge tier="pro" isLocked={isEstimateLocked} />
          <span className="flex-1" />
          <span className="text-list-item-subtitle text-muted-foreground truncate max-w-[140px]">
            {estimateRowValue}
          </span>
          <ChevronRight className="h-4 w-4 text-muted-foreground flex-shrink-0 rtl:-scale-x-100" />
        </button>

        <div className="border-t border-border/30 mx-5" />

        {SECONDARY_SECTION_TOGGLE_ROWS.map(renderSectionToggleRow)}

        {/* Requirements — PRO-gated; shapes WHAT the album contains, so it
            sits in the Settings group between the content-section toggles
            and Units (Apple parity; mirrors the two-column AlbumTabContent).
            The row reads like any other settings row on every plan — never
            dimmed/disabled for the plan gate — and the ProBadge is the only
            locked marker. A locked tap routes to the Pro Pass paywall; with
            access it opens the requirements editor. */}
        {!isArchived && (
          <>
            <button
              onClick={
                hasAdvancedAlbumOptions
                  ? () => setIsRequirementsOpen(true)
                  : () => openPassPaywall('pro')
              }
              className="w-full flex items-center gap-3 px-5 py-3 transition-colors hover:bg-muted/50"
            >
              <ClipboardList className="h-5 w-5 text-primary shrink-0" strokeWidth={1.5} />
              {/* Apple parity: PRO badge sits to the LEFT, right after the
                  row title — not on the trailing edge. */}
              <span className="text-list-item-subtitle text-left">
                {t('settings.requirements.label')}
              </span>
              <ProBadge tier="pro" isLocked={!hasAdvancedAlbumOptions} />
              <span className="flex-1" />
              {hasAdvancedAlbumOptions && requirements && (
                <span className="text-list-item-subtitle text-muted-foreground truncate max-w-[120px]">
                  {requirements.substring(0, 40)}
                  {requirements.length > 40 ? '...' : ''}
                </span>
              )}
              <ChevronRight className="h-4 w-4 text-muted-foreground flex-shrink-0 rtl:-scale-x-100" />
            </button>

            <div className="border-t border-border/30 mx-5" />
          </>
        )}

        {/* Units Row — measurement system for the album content (Auto /
            Metric / Imperial). Free for everyone; Auto (default) lets the
            server pick and omits the field from the generate request. Last
            Settings-group row — mirrors Apple/Android. */}
        <button
          onClick={() => setIsUnitsOpen(true)}
          disabled={isGenerating}
          className="w-full flex items-center gap-3 px-5 py-3 hover:bg-muted/50 transition-colors"
        >
          <PencilRuler className="h-5 w-5 text-primary shrink-0" strokeWidth={1.5} />
          <span className="text-list-item-subtitle flex-1 text-left">
            {t('settings.units.label')}
          </span>
          <span className="text-list-item-subtitle text-muted-foreground">
            {t(`settings.units.${measurementSystem}`)}
          </span>
          <ChevronRight className="h-4 w-4 text-muted-foreground flex-shrink-0 rtl:-scale-x-100" />
        </button>
      </SettingsCard>

      {/* Controls group — generation controls (HOW the album is built):
          Extended thinking. Requirements moved up into the Settings group
          (Apple parity — it shapes WHAT the album contains). */}
      {!isArchived && (
        <SettingsCard>
          {/* Extended thinking toggle — Pro+ gated. Locked tap routes to the
              server-driven Pro paywall; functional Switch otherwise. Mirrors
              the render ExtendedThinkingSection. Reuses render copy. */}
          <div
            className="px-5 py-3 cursor-pointer hover:bg-muted/50 transition-colors"
            onClick={handleExtendedThinkingRowClick}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleExtendedThinkingRowClick();
              }
            }}
          >
            <div className="flex items-center gap-3">
              <Brain
                className="h-5 w-5 text-primary flex-shrink-0"
                strokeWidth={1.5}
              />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-list-item-subtitle text-foreground whitespace-nowrap">
                    {tThinking('title')}
                  </span>
                  <ProBadge tier="pro" isLocked={!hasExtendedThinking} />
                </div>
              </div>
              <div
                // Stop the row's click handler so the user can tap the switch
                // itself without triggering an extra toggle on the parent row.
                onClick={(e) => e.stopPropagation()}
                onKeyDown={(e) => e.stopPropagation()}
              >
                <Switch
                  checked={hasExtendedThinking && isExtendedThinking}
                  onCheckedChange={handleExtendedThinkingSwitchChange}
                  disabled={!hasExtendedThinking || isGenerating}
                  aria-label={tThinking('title')}
                />
              </div>
            </div>
          </div>
        </SettingsCard>
      )}

      {/* Significant-cost warning — server-computed flag + server-localized
          text (parity with Apple): shown right before the Generate button only
          when the current settings make generation token-expensive. */}
      {costWarningMessage != null && !isGenerating && (
        <NoticeBanner variant="warning" message={costWarningMessage} />
      )}

      {/* Template NestedSheet */}
      <NestedSheet open={isTemplateOpen} onOpenChange={setIsTemplateOpen}>
        <NestedSheetContent>
          <NestedSheetHeader>
            <NestedSheetTitle>{t('settings.type.label')}</NestedSheetTitle>
          </NestedSheetHeader>
          <NestedSheetBody className="px-4">
            {/* Template = design axis. `standard` is free; `architect`/`studio`
                are premium (Designer-tier). Without Designer access a premium
                row shows a DESIGNER lock badge and a tap routes to the Designer
                Pass paywall; with access the row is freely selectable (no
                badge). Mirrors the closed named-model row. */}
            <div className="space-y-2">
              {ALBUM_TEMPLATE_OPTIONS.map((option) => {
                const isPremium = isPremiumAlbumTemplate(option);
                const locked = isPremium && !isAlbumTemplatesAvailable;
                const isSelected = !locked && selectedTemplate === option;
                return (
                  <button
                    key={option}
                    type="button"
                    onClick={() => {
                      // Locked premium template (no Designer access) → upsell
                      // the Designer Pass paywall, never select. Otherwise the
                      // row is selectable (incl. architect/studio for Designer
                      // holders). Mirrors the closed named-model row.
                      if (locked) {
                        openPassPaywall('designer');
                        return;
                      }
                      onTemplateChange(option);
                      setIsTemplateOpen(false);
                    }}
                    disabled={isGenerating}
                    className={cn(
                      'w-full flex flex-col gap-1 p-4 rounded-lg border-2 transition-colors text-left',
                      isSelected
                        ? 'border-primary bg-primary/5'
                        : 'border-border',
                      locked
                        ? 'opacity-50'
                        : 'hover:bg-muted/50'
                    )}
                  >
                    <div className="flex items-center justify-between w-full gap-2">
                      <span className="text-list-item-subtitle">
                        {t(`settings.type.${option}`)}
                      </span>
                      {/* DESIGNER lock badge on premium templates only while
                          locked — ProBadge renders nothing once unlocked. */}
                      {isPremium && (
                        <ProBadge tier="designer" isLocked={locked} />
                      )}
                      {isSelected && (
                        <Check className="h-4 w-4 text-primary flex-shrink-0" />
                      )}
                    </div>
                    {option === 'standard' && (
                      <span className="text-card-meta text-muted-foreground">
                        {t('settings.type.standardDescription')}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </NestedSheetBody>
        </NestedSheetContent>
      </NestedSheet>

      {/* Requirements NestedSheet */}
      <NestedSheet
        open={isRequirementsOpen}
        onOpenChange={setIsRequirementsOpen}
      >
        <NestedSheetContent>
          <NestedSheetHeader>
            <NestedSheetTitle>
              {t('settings.requirements.label')}
            </NestedSheetTitle>
          </NestedSheetHeader>
          <NestedSheetBody className="p-4">
            <Textarea
              value={requirements}
              onChange={(e) => onRequirementsChange(e.target.value)}
              placeholder={t('settings.requirements.placeholder')}
              className="min-h-[200px] resize-none"
              maxLength={2000}
              disabled={isArchived || isGenerating}
            />
          </NestedSheetBody>
        </NestedSheetContent>
      </NestedSheet>

      {/* Estimate NestedSheet (FE4) — this design's estimates, "New estimate",
          then sibling designs' estimates greyed at the end. */}
      <NestedSheet open={isEstimateOpen} onOpenChange={setIsEstimateOpen}>
        <NestedSheetContent>
          <NestedSheetHeader>
            <NestedSheetTitle>{tEstimate('album.label')}</NestedSheetTitle>
          </NestedSheetHeader>
          <NestedSheetBody className="px-4">
            <AlbumEstimateOptions
              currentDesignEstimates={currentDesignEstimates}
              otherDesignEstimates={otherDesignEstimates}
              selectedEstimateId={selectedEstimateId}
              isEstimateIncluded={isEstimateIncluded}
              onSelect={(id) => {
                setSelectedEstimateId(id);
                setIsEstimateOpen(false);
              }}
              onExclude={() => {
                excludeEstimate();
                setIsEstimateOpen(false);
              }}
              disabled={isGenerating}
            />
          </NestedSheetBody>
        </NestedSheetContent>
      </NestedSheet>

      {/* Language NestedSheet */}
      <NestedSheet open={isLanguageOpen} onOpenChange={setIsLanguageOpen}>
        <NestedSheetContent>
          <NestedSheetHeader>
            <NestedSheetTitle>{t('settings.language')}</NestedSheetTitle>
          </NestedSheetHeader>
          <NestedSheetBody className="px-4">
            <div className="space-y-2">
              {languageSheetOrder.map((lang) => {
                const isSelected = lang.code === selectedLanguage;
                return (
                  <button
                    key={lang.code}
                    onClick={() => {
                      onLanguageChange(lang.code);
                      setIsLanguageOpen(false);
                    }}
                    className={cn(
                      'w-full flex items-center justify-between p-4 rounded-lg border-2 transition-colors text-left',
                      isSelected
                        ? 'border-primary bg-primary/5'
                        : 'border-border hover:bg-muted/50'
                    )}
                  >
                    <span className="text-list-item-subtitle">
                      {lang.name} ({lang.nativeName})
                    </span>
                    {isSelected && (
                      <Check className="h-4 w-4 text-primary flex-shrink-0" />
                    )}
                  </button>
                );
              })}
            </div>
          </NestedSheetBody>
        </NestedSheetContent>
      </NestedSheet>

      {/* Units NestedSheet — Auto (server decides) / Metric / Imperial. */}
      <NestedSheet open={isUnitsOpen} onOpenChange={setIsUnitsOpen}>
        <NestedSheetContent>
          <NestedSheetHeader>
            <NestedSheetTitle>{t('settings.units.label')}</NestedSheetTitle>
          </NestedSheetHeader>
          <NestedSheetBody className="px-4">
            <div className="space-y-2">
              {ALBUM_MEASUREMENT_SYSTEM_OPTIONS.map((option) => {
                const isSelected = measurementSystem === option;
                return (
                  <button
                    key={option}
                    type="button"
                    onClick={() => {
                      onMeasurementSystemChange(option);
                      setIsUnitsOpen(false);
                    }}
                    disabled={isGenerating}
                    className={cn(
                      'w-full flex items-center justify-between p-4 rounded-lg border-2 transition-colors text-left',
                      isSelected
                        ? 'border-primary bg-primary/5'
                        : 'border-border hover:bg-muted/50'
                    )}
                  >
                    <span className="text-list-item-subtitle">
                      {t(`settings.units.${option}`)}
                    </span>
                    {isSelected && (
                      <Check className="h-4 w-4 text-primary flex-shrink-0" />
                    )}
                  </button>
                );
              })}
            </div>
          </NestedSheetBody>
        </NestedSheetContent>
      </NestedSheet>
    </div>
  );
}
